import React, { useState, useEffect, useCallback } from "react"
import { Link } from "react-router-dom"
import { authFetch, buildQuery } from "../../lib/api"

const STATUSES = ["pending", "confirmed", "cancelled", "completed"]

function formatDate(d) {
  if (!d) return "Any date"
  return new Date(d).toLocaleDateString(undefined, { dateStyle: "medium" })
}

export default function AdminBookings() {
  const [list, setList] = useState([])
  const [users, setUsers] = useState([])
  const [listings, setListings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [filterStatus, setFilterStatus] = useState("")
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ userId: "", listingId: "", type: "nightly", startDate: "", endDate: "", status: "pending" })
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setError("")
    const q = buildQuery({ page: 1, limit: 100, status: filterStatus || undefined })
    authFetch(`/api/admin/bookings${q}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) setError(data.error)
        else setList(data.bookings || [])
      })
      .catch(() => setError("Failed to load bookings"))
      .finally(() => setLoading(false))
  }, [filterStatus])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    authFetch("/api/admin/users?limit=500").then((r) => r.json()).then((d) => setUsers(d.users || [])).catch(() => {})
    authFetch("/api/admin/listings?limit=500").then((r) => r.json()).then((d) => setListings(d.listings || [])).catch(() => {})
  }, [])

  const handleStatusChange = async (id, status) => {
    const res = await authFetch(`/api/admin/bookings/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    })
    if (res.ok) load()
    else {
      const data = await res.json()
      alert(data.error || "Update failed")
    }
  }

  const handleCreateSubmit = async (e) => {
    e.preventDefault()
    if (!createForm.userId || !createForm.listingId || !createForm.startDate || !createForm.endDate) {
      alert("User, listing, start date, and end date are required")
      return
    }
    setSaving(true)
    try {
      const res = await authFetch("/api/admin/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createForm),
      })
      const data = await res.json()
      if (!res.ok) {
        alert(data.error || "Create failed")
        return
      }
      setShowCreate(false)
      setCreateForm({ userId: "", listingId: "", type: "nightly", startDate: "", endDate: "", status: "pending" })
      load()
    } catch (err) {
      alert("Request failed")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Cancel this booking?")) return
    const res = await authFetch(`/api/admin/bookings/${id}`, { method: "DELETE" })
    if (res.ok) load()
  }

  return (
    <section className='dashboard-card'>
      <div className='dashboard-card__header'>
        <div>
          <span className='dashboard-eyebrow'>Reservations</span>
          <h2>Bookings</h2>
          <p>View all bookings, filter by status, update statuses, or create a booking.</p>
        </div>
        <button type='button' className='dashboard-primary-action admin-primary-action' onClick={() => setShowCreate(true)}>
          <i className='fa-solid fa-calendar-plus'></i>
          Create booking
        </button>
      </div>

      {error && <p className='dashboard-error'>{error}</p>}

      <div className='admin-toolbar'>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value=''>All statuses</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
      </div>

      {showCreate && (
        <form onSubmit={handleCreateSubmit} className='admin-form admin-form--wide'>
          <h3>New booking</h3>
          <label>
            User *
            <select required value={createForm.userId} onChange={(e) => setCreateForm((f) => ({ ...f, userId: e.target.value }))}>
              <option value=''>Select user</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </select>
          </label>
          <label>
            Listing *
            <select required value={createForm.listingId} onChange={(e) => setCreateForm((f) => ({ ...f, listingId: e.target.value }))}>
              <option value=''>Select listing</option>
              {listings.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </label>
          <label>
            Type
            <select value={createForm.type} onChange={(e) => setCreateForm((f) => ({ ...f, type: e.target.value }))}>
              <option value='nightly'>nightly</option>
              <option value='weekly'>weekly</option>
              <option value='monthly'>monthly</option>
            </select>
          </label>
          <label>
            Status
            <select value={createForm.status} onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}>
              {STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </label>
          <label>
            Start date *
            <input type='date' required value={createForm.startDate} onChange={(e) => setCreateForm((f) => ({ ...f, startDate: e.target.value }))} />
          </label>
          <label>
            End date *
            <input type='date' required value={createForm.endDate} onChange={(e) => setCreateForm((f) => ({ ...f, endDate: e.target.value }))} />
          </label>
          <div className='admin-form__actions'>
            <button type='submit' className='dashboard-primary-action admin-primary-action' disabled={saving}>
              {saving ? "Saving..." : "Create"}
            </button>
            <button type='button' className='btn btn--secondary' onClick={() => setShowCreate(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : list.length === 0 ? (
        <div className='dashboard-empty'>No bookings.</div>
      ) : (
        <div className='admin-table-wrap'>
          <table className='admin-table'>
            <thead>
              <tr>
                <th>User</th>
                <th>Listing</th>
                <th>Type</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((booking) => (
                <tr key={booking.id}>
                  <td>
                    <strong>{booking.userName || booking.userEmail || booking.userId}</strong>
                  </td>
                  <td>{booking.listing ? <Link to={`/listings/${booking.listingId}`}>{booking.listing.name}</Link> : booking.listingId}</td>
                  <td>{booking.type}</td>
                  <td>{formatDate(booking.startDate)} - {formatDate(booking.endDate)}</td>
                  <td>
                    <select value={booking.status} onChange={(e) => handleStatusChange(booking.id, e.target.value)}>
                      {STATUSES.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className='dashboard-list__actions'>
                      <button type='button' className='btn btn--danger' onClick={() => handleDelete(booking.id)}>
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
